/** @format */

import React from 'react'
import { _$, Step, Steps, Tag, Copy } from '@king-design/react'
import styled from 'styled-components'
import CodeDisplay from '../../../../components/CodeDisplay'
import { renderHTTPAddress } from './PythonAccess'

const OpenTelemetry: React.FC<{
  className?: string
  visible?: boolean
  appKey: string
  region: string
}> = ({ className, visible, appKey, region }) => {
  const installCommand =
    "pip install 'opentelemetry-api>=1.20.0' 'opentelemetry-sdk>=1.20.0' 'opentelemetry-exporter-otlp>=1.20.0'"
  const codeExample = `# 初始化 OpenTelemetry logs
from opentelemetry._logs import set_logger_provider
from opentelemetry.sdk._logs import LoggerProvider, LoggingHandler
from opentelemetry.sdk._logs.export import SimpleLogRecordProcessor
from opentelemetry.exporter.otlp.proto.http._log_exporter import OTLPLogExporter

otlp_endpoint = os.getenv("OTEL_EXPORTER_OTLP_ENDPOINT", "https://${region}.otlp.ksyun.com:4317")

logger_provider = LoggerProvider()
log_exporter = OTLPLogExporter(
    endpoint=f"{otlp_endpoint}/v1/logs",
    headers={"ksc-appkey": "********"},
)
logger_provider.add_log_record_processor(SimpleLogRecordProcessor(log_exporter))
set_logger_provider(logger_provider)

# 初始化 OpenTelemetry traces
from opentelemetry import trace
from opentelemetry.sdk.trace import TracerProvider
from opentelemetry.sdk.trace.export import BatchSpanProcessor
from opentelemetry.sdk.resources import Resource
from opentelemetry.exporter.otlp.proto.http.trace_exporter import OTLPSpanExporter

resource = Resource.create({
    "service.name": os.getenv("OTEL_SERVICE_NAME", "nanoagent"),
})

tracer_provider = TracerProvider(resource=resource)
span_exporter = OTLPSpanExporter(
    endpoint=f"{otlp_endpoint}/v1/traces",
    headers={"ksc-appkey": "********"},
)
tracer_provider.add_span_processor(BatchSpanProcessor(span_exporter))
trace.set_tracer_provider(tracer_provider)

tracer = trace.get_tracer(__name__)

# 初始化 OpenTelemetry metrics
from opentelemetry import metrics
from opentelemetry.sdk.metrics import MeterProvider
from opentelemetry.sdk.metrics.export import PeriodicExportingMetricReader
from opentelemetry.exporter.otlp.proto.http.metric_exporter import OTLPMetricExporter

metric_exporter = OTLPMetricExporter(
    endpoint=f"{otlp_endpoint}/v1/metrics",
    headers={"ksc-appkey": "********"},
)
metric_reader = PeriodicExportingMetricReader(metric_exporter)
meter_provider = MeterProvider(
    resource=resource,
    metric_readers=[metric_reader],
)
metrics.set_meter_provider(meter_provider)

meter = metrics.get_meter(__name__)

# Token 使用计数器
token_counter = meter.create_counter(
    name="llm.token.usage",
    description="LLM token usage count",
    unit="1",
)


logging.basicConfig(level=logging.INFO)
logger = logging.getLogger(__name__)
logger.addHandler(LoggingHandler(logger_provider=logger_provider))

def record_token_usage(response, step: str):
    """从 LLM 响应中提取并上报 token 使用量"""
    usage = getattr(response, "usage_metadata", None)
    if usage is None:
        usage = getattr(response, "response_metadata", {}).get("token_usage")
    if usage is None:
        logger.warning(f"[Metrics] step={step} 未找到 token usage 数据")
        return
    if isinstance(usage, dict):
        input_tokens = usage.get("input_tokens", 0)
        output_tokens = usage.get("output_tokens", 0)
    else:
        logger.warning(f"[Metrics] step={step} token usage 格式不支持: {type(usage)}")
        return
    token_counter.add(input_tokens, {"model": model, "step": step, "type": "input"})
    token_counter.add(output_tokens, {"model": model, "step": step, "type": "output"})
    logger.info(f"[Metrics] step={step} input_tokens={input_tokens} output_tokens={output_tokens}")

def call_tools(state: AgentState) -> AgentState:
    """执行工具调用"""
    with tracer.start_as_current_span("call_tools") as span:
        # 设置session id
        span.set_attribute("gen_ai.conversation_id", "session-001")
        logger.info("[Node] 执行工具")

        messages = state["messages"]
        last_message = messages[-1]

        tool_results = []
        if hasattr(last_message, "tool_calls"):
            for tool_call in last_message.tool_calls:
                tool_name = tool_call["name"]
                tool_args = tool_call["args"]
                tool_id = tool_call["id"]

                logger.info(f"调用工具: {tool_name}({tool_args})")

                if tool_name in tools_by_name:
                    result = tools_by_name[tool_name].invoke(tool_args)
                    tool_results.append(ToolMessage(content=result, tool_call_id=tool_id))
                    span.add_event("tool_call", {"tool.name": tool_name, "tool.result": result})
                    logger.info(f"工具结果: {result}")

        span.set_attribute("tool.call_count", len(tool_results))
        return {
            "messages": list(messages) + tool_results,
            "next_step": "generate_response",
            "tool_result": "",
        }`

  return (
    <Steps className={className} vertical type="simple">
      <Step title={_$('一、安装OpenTelemetry SDK')}>
        <>
          <p>{_$('通过pip安装OpenTelemetry SDK，要求python版本大于等于3.9')}</p>
          <CodeDisplay
            content={installCommand}
            language="shell"
            lineNumbers
            refreshTrigger={visible}
            height={60}
          />
        </>
      </Step>
      <Step title={_$('二、参考下面的代码示例，正确设置http header和otlp endpoint')}>
        <>
          <p>
            {_$('在exporter的')}
            <Tag>http headers</Tag>
            {_$('中正确设置')}
            <Tag>app-key</Tag>，{_$('格式如下')}： ksc-appkey：********
            <Copy text={appKey} />
          </p>
          {renderHTTPAddress(region)}
          <CodeDisplay
            content={codeExample}
            copyContent={appKey ? codeExample.replace(/\*{8}/g, appKey) : codeExample}
            language="python"
            height={620}
            lineNumbers
            refreshTrigger={visible}
          />
        </>
      </Step>
      <Step title={_$('三、在云监控中观测是否有trace上报')} />
    </Steps>
  )
}

export default styled(OpenTelemetry)``
